// src/components/Studio/StudioHero.tsx
import type { ReactNode } from 'react';
import { BRAND_GRADIENT_TEXT, CONTAINER, EYEBROW } from './studioStyles';

interface StudioHeroProps {
  eyebrow: string;
  title: string;
  /** Rendered in the brand gradient, appended to the title. */
  accent?: string;
  lede?: string;
  actions?: ReactNode;
  /** Optional stat strip rendered under the lede. */
  stats?: Array<{ label: string; value: string }>;
  backgroundUrl?: string | null;
}

const StudioHero = ({
  eyebrow,
  title,
  accent,
  lede,
  actions,
  stats,
  backgroundUrl,
}: StudioHeroProps) => (
  <section className="relative overflow-hidden border-b border-white/10">
    {backgroundUrl && (
      <>
        <img
          src={backgroundUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D] via-[#0B0B0D]/85 to-[#0B0B0D]/60" />
      </>
    )}

    <div className={`${CONTAINER} relative py-20 sm:py-24`}>
      <p className={EYEBROW}>{eyebrow}</p>

      <h1 className="heading-cinematic mt-4 max-w-4xl text-4xl text-white sm:text-5xl lg:text-6xl">
        {title}
        {accent && (
          <>
            {' '}
            <span className={BRAND_GRADIENT_TEXT}>{accent}</span>
          </>
        )}
      </h1>

      {lede && (
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-400 sm:text-lg">
          {lede}
        </p>
      )}

      {actions && <div className="mt-9 flex flex-wrap items-center gap-3">{actions}</div>}

      {stats && stats.length > 0 && (
        <dl className="mt-12 grid max-w-3xl grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label}>
              <dt className="text-xs uppercase tracking-wider text-neutral-500">{stat.label}</dt>
              <dd className="mt-1 font-display text-3xl font-bold text-white">{stat.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  </section>
);

export default StudioHero;
